import { useAuth0 } from "@auth0/auth0-vue";
import { createRouter, createWebHistory } from "@ionic/vue-router";
import { RouteRecordRaw } from "vue-router";

import { useAuthStore } from "@/stores/auth";

export const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/dashboard",
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/LoginPage.vue"),
  },
  {
    path: "/calendar",
    component: () => import("@/views/calendar/CalendarPage.vue"),
  },
  {
    path: "/clients",
    component: () => import("@/views/client/ClientListPage.vue"),
  },
  {
    path: "/clients/new",
    component: () => import("@/views/client/ClientEditPage.vue"),
  },
  {
    path: "/clients/:id",
    props: true,
    component: () => import("@/views/client/ClientDetailPage.vue"),
  },
  {
    path: "/clients/:id/edit",
    props: true,
    component: () => import("@/views/client/ClientEditPage.vue"),
  },
  {
    path: "/dashboard",
    component: () => import("@/views/DashboardPage.vue"),
  },
  {
    path: "/horses",
    component: () => import("@/views/horse/HorseListPage.vue"),
  },
  {
    path: "/horses/new",
    component: () => import("@/views/horse/HorseEditPage.vue"),
  },
  {
    path: "/horses/:id",
    props: true,
    component: () => import("@/views/horse/HorseDetailPage.vue"),
  },
  {
    path: "/horses/:id/edit",
    props: true,
    component: () => import("@/views/horse/HorseEditPage.vue"),
  },
  {
    path: "/stables",
    component: () => import("@/views/stable/StableListPage.vue"),
  },
  {
    path: "/stables/new",
    component: () => import("@/views/stable/StableEditPage.vue"),
  },
  {
    path: "/stables/map",
    component: () => import("@/views/stable/StableMapPage.vue"),
  },
  {
    path: "/stables/:id",
    props: true,
    component: () => import("@/views/stable/StableDetailPage.vue"),
  },
  {
    path: "/stables/:id/edit",
    props: true,
    component: () => import("@/views/stable/StableEditPage.vue"),
  },

  {
    path: "/trims/new",
    component: () => import("@/views/trim/TrimEditPage.vue"),
  },
  {
    path: "/trims/:id",
    props: true,
    component: () => import("@/views/trim/TrimDetailPage.vue"),
  },
  {
    path: "/trims/:id/edit",
    props: true,
    component: () => import("@/views/trim/TrimEditPage.vue"),
  },
  {
    path: "/profile",
    name: "profile",
    component: () => import("@/views/ProfilePage.vue"),
  },
  {
    path: "/subscription",
    name: "subscription",
    component: () => import("@/views/SubscriptionPage.vue"),
  },
  {
    path: "/settings",
    component: () => import("@/views/SettingsPage.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

async function waitForAuth0(auth0: ReturnType<typeof useAuth0>): Promise<void> {
  if (!auth0.isLoading.value) return;

  return new Promise<void>(resolve => {
    const interval = setInterval(() => {
      if (!auth0.isLoading.value) {
        clearInterval(interval);
        resolve();
      }
    }, 50);
  });
}

export async function checkAuth(): Promise<boolean> {
  const auth0 = useAuth0();
  const authStore = useAuthStore();

  await waitForAuth0(auth0);

  if (authStore.isOnline && auth0.isAuthenticated.value) {
    if (auth0.user.value) {
      authStore.cacheUser(auth0.user.value);
    }
    return true;
  }

  return authStore.hasValidCache;
}

// Navigation guard for offline-first authentication
router.beforeEach(async to => {
  if (to.path === "/login") {
    return true;
  }

  const isAuthenticated = await checkAuth();

  return isAuthenticated ? true : { path: "/login" };
});

export default router;
